import { Box, SxProps } from '@mui/material';
import { CSSProperties, FC, useState } from 'react';

import Image from 'next/image';
import { MUIResponsiveValue } from '@/logic/types';
import MuviFallBackImage from '/public/icons/shared/logo-share-og.png';
import Skeleton from '@mui/material/Skeleton';

interface ImageBoxProps {
  img: string | File | FC;
  fallBackImg?: File | string;
  title?: string;
  width?: string | number;
  height?: string | number;
  maxWidth?: MUIResponsiveValue<string | number>;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none';
  sx?: SxProps;
  debug?: boolean;
  imageId?: string;
  style?: CSSProperties;
  objectPosition?: string | number;
  layout?: 'fill' | 'fixed' | 'intrinsic' | 'responsive';
}

export const ImageBoxFuture: FC<ImageBoxProps> = ({
  img,
  fallBackImg,
  title = '',
  width,
  height,
  sx,
  objectFit,
  maxWidth,
  debug = true,
  layout,
  style,
  objectPosition,
  imageId,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [onErrorSrc, setOnErrorSrc] = useState<string | File | undefined>(undefined);

  const handleOnError = (e: React.SyntheticEvent<HTMLImageElement, Event>): void => {
    e?.currentTarget?.src !== MuviFallBackImage.src &&
      setOnErrorSrc(fallBackImg || MuviFallBackImage.src);
  };

  return (
    <Box
      component='figure'
      sx={{
        position: 'relative',
        lineHeight: 0,
        objectFit,
        maxWidth,
        userSelect: 'none',
        ...sx,
      }}
    >
      {loading ? (
        <Skeleton
          variant='rectangular'
          animation='wave'
          sx={{
            ...sx,
            position: 'relative',
            zIndex: !!debug && !!loading ? 1 : 'auto',
          }}
        >
          <Image
            alt={title}
            src={(onErrorSrc || img) as string}
            width={width}
            height={height}
            onLoadingComplete={() => setLoading(false)}
            onError={e => handleOnError(e)}
            layout={layout || 'fill'}
            style={style}
            objectPosition={objectPosition}
            objectFit={onErrorSrc ? 'contain' : objectFit}
            loading='lazy'
          />
        </Skeleton>
      ) : (
        <Image
          id={imageId || 'image'}
          alt={title}
          src={(onErrorSrc || img) as string}
          width={width}
          height={height}
          onLoadingComplete={() => setLoading(false)}
          onError={e => handleOnError(e)}
          layout={layout || 'fill'}
          style={style}
          objectPosition={objectPosition}
          objectFit={onErrorSrc ? 'contain' : objectFit}
          loading='lazy'
        />
      )}
    </Box>
  );
};

export default ImageBoxFuture;
